/**
 * QUIPWA — i18n store.
 *
 * Zustand rather than a React context, to match the other stores here and so
 * strings can also be resolved outside a component (see `translate`).
 *
 * The UI language is a device preference, not user data: it lives in
 * localStorage and is deliberately not synced to Drive, so the same account can
 * read Spanish on one machine and English on another.
 */

import { create } from 'zustand';
import { useCallback } from 'react';
import { CATALOGS, LOCALES, interpolate, type Locale, type TranslationKey } from './translations.ts';

const STORAGE_KEY = 'quipwa:locale';

function isLocale(value: string | null): value is Locale {
  return value != null && (LOCALES as readonly string[]).includes(value);
}

/** Stored choice, else the browser's language, else Spanish. */
function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private mode / storage disabled — fall through to the browser hint.
  }

  const browser = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : '';
  return isLocale(browser) ? browser : 'es';
}

/**
 * Resolve a key without React. Falls back to the key itself, which is far
 * easier to spot in the UI than an empty string.
 */
export function translate(
  locale: Locale,
  key: TranslationKey,
  vars?: Record<string, string | number>
): string {
  const template = CATALOGS[locale][key] ?? key;
  return interpolate(template, vars);
}

interface I18nState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useI18nStore = create<I18nState>((set) => ({
  locale: detectLocale(),
  setLocale: (locale) => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Not persisting is survivable; the session still switches.
    }
    // Keep the document in sync so screen readers and `:lang()` rules follow.
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
    set({ locale });
  },
}));

// Apply the initial language to <html> once, at module load.
if (typeof document !== 'undefined') {
  document.documentElement.lang = useI18nStore.getState().locale;
}

export type TFunction = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * Translation hook. `t` is memoised per locale, so it's stable across renders
 * but still changes identity when the language does — which is what lets
 * components memoised on `t` re-render on a switch.
 */
export function useI18n(): { t: TFunction; locale: Locale; setLocale: (l: Locale) => void } {
  const locale = useI18nStore((s) => s.locale);
  const setLocale = useI18nStore((s) => s.setLocale);
  const t = useCallback<TFunction>((key, vars) => translate(locale, key, vars), [locale]);
  return { t, locale, setLocale };
}
